import { useTranslation } from 'react-i18next';
import type { EventRecord } from '@dairy/shared';
import { Badge } from '@/components/ui';
import { useFormat } from '@/lib/format';

/** One-line description of an event for timelines and lists. */
export function EventSummary({ event: e, userName }: { event: EventRecord; userName?: string }) {
  const { t } = useTranslation();
  const f = useFormat();
  const d = e.data as Record<string, unknown>;
  const parts: string[] = [];
  switch (e.type) {
    case 'HEAT':
      parts.push(((d.signs as string[] | undefined) ?? []).map((x) => t(`events.heatSign.${x}`)).join(', '));
      break;
    case 'SERVICE':
      parts.push(t(`events.serviceMethod.${String(d.method)}`));
      if (d.technician) parts.push(String(d.technician));
      if (d.sireCode) parts.push(String(d.sireCode));
      break;
    case 'PREG_CHECK':
      parts.push(t(`events.checkResult.${String(d.result)}`));
      break;
    case 'CALVING': {
      const calves = (d.calves as Array<{ sex: string; alive: boolean }> | undefined) ?? [];
      parts.push(calves.map((c) => `${t(`animals.${c.sex === 'FEMALE' ? 'female' : 'male'}`)}${c.alive ? '' : ` (${t('events.dead')})`}`).join(', '));
      if (d.ease) parts.push(t(`events.ease.${String(d.ease)}`));
      break;
    }
    case 'DRY_OFF':
      if (d.reason) parts.push(t(`events.dryReason.${String(d.reason)}`));
      break;
    case 'ABORTION':
      if (d.monthsPregnant) parts.push(t('events.monthsPregnantValue', { count: Number(d.monthsPregnant) }));
      break;
    case 'TREATMENT':
      parts.push(d.problem === 'OTHER' ? String(d.problemOther ?? '') : t(`events.problem.${String(d.problem)}`));
      parts.push(String(d.drug ?? ''));
      if (Number(d.milkWithdrawalDays) > 0) parts.push(t('events.withdrawalDaysValue', { count: Number(d.milkWithdrawalDays) }));
      break;
    case 'VACCINATION':
    case 'DEWORMING':
      parts.push(String(d.product ?? ''));
      break;
    case 'WEIGHT':
      parts.push(`${String(d.kg)} kg`);
      break;
    case 'NOTE':
      parts.push(String(d.text ?? ''));
      break;
  }
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{t(`events.type.${e.type}`)}</span>
        {e.isOpening ? <Badge>{t('events.opening')}</Badge> : null}
        {e.costSantim ? <span className="text-sm text-stone-600">{f.birr(e.costSantim)}</span> : null}
      </div>
      <div className="text-sm text-stone-700">{parts.filter(Boolean).join(' · ')}</div>
      <div className="text-sm text-stone-500">
        {f.date(e.date)}
        {userName ? ` · ${userName}` : ''}
        {e.note ? ` · ${e.note}` : ''}
      </div>
    </div>
  );
}
